<template>
  <div class="opc-cart__total">
    <div class="opc-cart__total__sub" v-if="showSubTotal">
      <div class="opc-cart__total__text">{{ $t('billing.order_summary.subtotal') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ subTotalAmountText }}
      </div>
    </div>
    <template v-for="item in totalDiscountList">
      <div class="opc-cart__total__discount" v-if="item.discount" :key="item.type">
        <div class="opc-cart__total__text" style="text-transform: unset !important;">
          <v-amount-loading>
            <span>{{ item.label }}</span>
          </v-amount-loading>
        </div>
        <v-amount-loading>
          <div class="opc-cart__total__text text-no-capitalize">{{ $money(item.discount) }}</div>
        </v-amount-loading>
      </div>
    </template>
    <div class="opc-cart__total__tax" v-if="isShowTax && isTaxEstimatedBefore">
      <div class="opc-cart__total__text __PLACEHOLDER_total__tax">
        {{ $t('billing.estimated_tax') }}
        <!-- tooltip start -->
        <zm-tooltip role="button" aria-label="learn more about Estimated Tax" :popper-options="{ boundariesElement: 'scrollParent' }" v-if="hasZoomDevice">
          <i class="zm-icon-info-outline" style="position: relative; top: -1px; color: #666487; font-size: 16px" />
          <div class="zm-tooltip__content zm-tooltip__content-total" slot="content">
            <p>{{ $t('billing.hw.desk_phones.tax.zoom_tax_amount', { amount: $money(getTaxTotalAmount) }) }}</p>
            <p>{{ $t('billing.hw.hardware_tax_amount', { amount: $money(getDeskphoneTaxTotalAmount) }) }}</p>
          </div>
        </zm-tooltip>
        <div class="tooltip-container only-show-in-success inline-block" data-position="top" v-if="hasZoomDevice">
          <i class="zm-icon-info-outline" style="position: relative; top: -1px; color: #666487; font-size: 16px"/>
          <div class="tooltip zm-tooltip__content">
            <p>{{ $t('billing.hw.desk_phones.tax.zoom_tax_amount', { amount: $money(getTaxTotalAmount) }) }}</p>
            <p>{{ $t('billing.hw.hardware_tax_amount', { amount: $money(getDeskphoneTaxTotalAmount) }) }}</p>
          </div>
        </div>
        <!-- tooltip end -->
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        <i v-if="isLoadingTax || isLoadingPreview" class="zm-icon-loading" />
        <span v-else-if="$context.is.delta">
          {{ this.$money(this.getProratedChargeTaxAmount) }}
        </span>
        <span v-else>
          {{ this.$money(this.getTaxTotalAmount + this.getDeskphoneTaxTotalAmount) }}
        </span>
      </div>
    </div>
    <inr-discount/>
    <refer-a-friend-discount/>
    <refer-a-friend-referral-credit />
    <div class="opc-cart__total__cba" v-if="showCreditBalance">
      <div class="opc-cart__total__text">{{ $t('billing.credit_balance') }}</div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(this.getCreditBalance) }}
      </div>
    </div>
    <div class="opc-cart__total__main" v-if="showProratedCharge">
      <div class="opc-cart__total__text">
        <total-label />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.proratedChargeTotalAmountText }}
      </div>
    </div>
    <div class="opc-cart__main__divider" v-if="showProratedCharge"></div>
    <div class="opc-cart__total__main" v-if="showTotal">
      <div class="opc-cart__total__text">
        <total-label :show-today-total-label="showTodayTotalLabel" />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        <v-amount-loading>
          {{ this.$money(includeTaxTotalAmount) }}
        </v-amount-loading>
        <p class="opc-cart__total__text__sub text-no-capitalize" v-if="showPlusTaxTxt">
          <priceVATTip />
          <span v-if="!showVATTip" class="opc-cart__total__tax-tip">{{ $t('billing.order_summary.plus_applicable_taxes') }}</span>
        </p>
      </div>
    </div>
    <next-charge v-else-if="!isPureFreeTrialFlow" />
    <!-- // FREE TRIAL - specific total charge display for free trial -->
    <div class="opc-cart__total__main" v-if="isPureFreeTrialFlow">
      <div class="opc-cart__total__text">
        <total-label />
      </div>
      <div class="opc-cart__total__text text-no-capitalize">
        {{ this.$money(0) }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import _ from 'lodash';
import NextCharge from './next.vue';
import TotalLabel from './total-label.vue';
import flow from '@/flows';
import { pageConfig } from '@/common/service/page-config';
import { planTypes, VIEWS, ALLOW_MIXED_BUY_FREE_TRIAL_PLANS, BILLING_CYCLE } from '@/common/constants';
import { plansof } from '@/utils/chain';
import InrDiscount from "./inr-discount";
import ReferAFriendDiscount from "./refer-a-friend-discount";
import ReferAFriendReferralCredit from './refer-a-friend-referral-credit/index.vue';
import { couponDiscountTypeMap } from '@/views/config/coupon/helper/constant';
import VAmountLoading from '@/components/v-amount-loading.vue';
import { logger } from '@/utils/log';
import { PROMOTION_TYPE } from '@/views/config/coupon/helper/constant';
import { getSoftBundlePromotionInfo } from '@/views/config/coupon/helper/promotion';

export default {
  components: {
    VAmountLoading,
    TotalLabel,
    NextCharge,
    InrDiscount,
    ReferAFriendDiscount,
    ReferAFriendReferralCredit
  },
  data() {
    return {};
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getSelectedPlansAmount',
      'getTotalCharge',
      'taxPayload',
      'getTaxTotalAmount',
      'getDeskphoneTaxTotalAmount',
      'isTaxExempt',
      'isTaxEstimatedBefore',
      'isLoadingTax',
      'discountWithAmountMap',
      'soldToContact',
      'selectedPlanListPayLoad',
      'addressInfo',
      'isInrCitEligible',
      'isProratedChargeAmountCalculated',
      'getProratedChargeTotalAmount',
      'getProratedChargeTaxAmount',
      'isLoadingPreview',
      'getCreditBalance',
      'openInvoiceTotalAmount',
      'includeTaxTotalAmount',
      'couponConfiguration',
      'softBundleUseByPriceEngine',
      'promotionDiscountInfoMap',
      'computedAmountLoading',
      'hasZoomDevice',
    ]),
    isFreeTrialFlow () {
      return this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    // BUY TRIAL
    // FREE TRIAL - specific total charge display for free trial
    isPureFreeTrialFlow() {
      return this.isFreeTrialFlow && !plansof(this.getSelectedPlans).not().freeTrial().has() ;
    },
    isMixedFreeTrialFlow() {
      return this.isFreeTrialFlow &&
        plansof(this.getSelectedPlans).type(...ALLOW_MIXED_BUY_FREE_TRIAL_PLANS).has() &&
        plansof(this.getSelectedPlans).not().freeTrial().has()
    },
    totalDiscountList() {
      const softBundleDiscount = getSoftBundlePromotionInfo()?.discountInfo?.totalDiscount ?? 0;

      const list = [
        {
          label: this.discountLabel,
          discount: this.discount,
          type: PROMOTION_TYPE.coupon,
        },
        {
          label: this.discountLabel,
          discount: softBundleDiscount,
          type: PROMOTION_TYPE.softBundle,
        }
      ]

      // todo [soft bundle]
      // multiple discount
      if (this.computedAmountLoading) {
        return _.filter(list, (item) => item.discount).slice(0, 1);
      }

      return list;
    },
    discount() {
      return this.discountWithAmountMap?.totalDiscount ?? 0;
    },
    isShowTax() {
      // FREE TRIAL - won't show tax fee for free trial, except it's under FCCA compliance
      if (this.isPureFreeTrialFlow && this.addressInfo.country !== 'DE') {
        return false;
      }
      // isTaxExempt in zuora account is only part of the whole tax amount
      if (flow.view() === VIEWS.COMPOUND || this.$context.is.modal) {
        return true;
      }
      if (this.$context.is.configPage) {
        if (this.isTaxEstimatedBefore) return true;
        return false;
      }
      return true;
    },
    showSubTotal() {
      return this.getSelectedPlans?.length > 1 || this.partnerShowSubTotal;
    },
    partnerShowSubTotal() {
      return this.$context.is.add2cart
        && [planTypes.MASTER_CLASS].includes(pageConfig?.editConfig?.planType)
        && this.getSelectedPlans?.length > 0;
    },
    partnerShowTotal() {
      return this.$context.is.add2cart
        && [planTypes.MASTER_CLASS].includes(pageConfig?.editConfig?.planType);
    },
    showTotal() {
      return this.$context.is.buyNow || this.$context.is.basic2pro || this.partnerShowTotal || this.isMixedFreeTrialFlow || this.showTotal4AddOneTimePlans;
    },
    showTodayTotalLabel() {
      return this.showTotal4AddOneTimePlans;
    },
    showTotal4AddOneTimePlans() {
      // for add flow | zs upgrade to ze ppa flow and the shopping cart only have one time/one month/ppa plan, will show today's total in the total section
      if (
        this.$context.is.add2cart
        || this.$context.is.zs2ze
        || this.$context.is.switch
      ) {
        return this.cartIsOnlyExistOneTimePlans;
      }

      return false;
    },
    cartIsOnlyExistOneTimePlans() {
      return _.every(this.getSelectedPlans, (p) => {
        return [BILLING_CYCLE.ONE_TIME, BILLING_CYCLE.ONE_MONTH, BILLING_CYCLE.PAY_PER_ATTENDEE].includes(p?.billing_cycle);
      })
    },
    showProratedCharge() {
      if (this.$context.is.delta) return true;
      return this.isInrCitEligible && this.isProratedChargeAmountCalculated;
    },
    showCreditBalance() {
      return this.isInrCitEligible && this.getCreditBalance > 0;
    },
    showPlusTaxTxt(){
      const is0total = ( this.getTotalCharge + this.getTaxTotalAmount === 0 )
      return !(this.isShowTax && this.isTaxEstimatedBefore) && !is0total
    },
    subTotalAmountText() {
      const subTotal = this.getSelectedPlansAmount.totalAmountWithoutDiscount + this.openInvoiceTotalAmount;
      return this.$money(Math.max(subTotal, 0));
    },
    proratedChargeTotalAmountText() {
      return this.$money(Math.max(this.getProratedChargeTotalAmount, 0));
    },
    discountLabel() {
      // [soft-bundle] todo need remove this after soft bundle use by price engine is released
      const discountPlans = this.softBundleUseByPriceEngine ?
        this.promotionDiscountInfoMap?.[PROMOTION_TYPE.softBundle]?.discountPlans :
        this.discountWithAmountMap?.discountPlans;
      return this.getSoftBundleDiscountLabel(discountPlans);
    }
  },
  methods: {
    ...mapMutations(['setIsLoadingTax', 'setIsTaxUpdateAttempted']),
    ...mapActions(['estimateTax']),
    getSoftBundleDiscountLabel(discountPlans) {
      if (this.couponConfiguration?.cartView?.showTotalDiscountPercentage) {
        // If discountPlans array has multiple different discount values, return default label
        const discounts = _.chain(discountPlans)
          .values()
          .map(p => p.discount)
          .uniq()
          .value();

        // multiple different discount values, return default label -> "discount"
        if (discounts.length > 1) {
          return this.$t('billing.coupon.discount');
        }

        const discountPercentage = _.chain(discountPlans)
          .values()
          .find((p = {}) => p.discount > 0 && p.discountType === couponDiscountTypeMap.percentage)
          .get('discount')
          .value();

        if (discountPercentage) {
          return this.$t('billing.order_summary.inr_discount_title', { discount: discountPercentage });
        }
      }

      return this.$t('billing.coupon.discount');
    },
    reloadTax: _.debounce(async function () {
      // TODO: get default ecm status before reload Tax
      const picked = _.pick(this.soldToContact, ['country', 'city', 'zip']);
      if (!this.isShowTax || !this.selectedPlanListPayLoad.length || Object.values(picked).some((x) => !x)) {
        this.setIsTaxUpdateAttempted(true);
        return;
      }
      try {
        this.setIsLoadingTax(true);
        await this.estimateTax();
      } catch (e) {
        logger.error(e)
        this.$message.error(e.message);
      } finally {
        this.setIsTaxUpdateAttempted(true);
        this.setIsLoadingTax(false);
      }
    }, 800),
  },
  mounted() {
    document.querySelectorAll('.tooltip-container').forEach(container => {
      const tooltip = container.querySelector('.tooltip');
      const position = container.getAttribute('data-position') || 'top';
      tooltip.setAttribute('data-position', position);

      container.addEventListener('mouseenter', () => {
        tooltip.classList.add('show');
      });
      container.addEventListener('mouseleave', () => {
        tooltip.classList.remove('show');
      });
    });
  },
  watch: {
    taxPayload: {
      deep: true,
      immediate: true,
      handler(nv, ov) {
        if(_.isEqual(nv, ov)) return;
        this.setIsTaxUpdateAttempted(false);
        this.reloadTax();
      },
    },
  },
};
</script>

<style lang="scss">
@import "total";
.zm-tooltip__content-total {
  padding: 8px 4px;
  p {
    // text-align: center;
    // width: 268px;
    color: var(--day-foreground-foreground-ada, #666487);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%; /* 16.8px */
  }
}
</style>
<style lang="scss" scoped>
.only-show-in-success {
  display: none;
}
</style>
